/* Sash Measure Widget — dimension-capture core.
 *
 * Product-agnostic: products come from config (see INTEGRATION.md). The stable core is
 * capturing window dimensions (+ optional consented photo) and attaching them to the order.
 *
 * Measurement is TAP-BASED (reliable) rather than auto edge-detection:
 *   1) tap the 4 corners of an A4 sheet (210x297mm) taped flat to the pane -> horizontal &
 *      vertical mm-per-pixel scales
 *   2) tap the two window edges -> width (horizontal scale)
 *   3) tap meeting-rail -> sill (half) or full window height -> height (vertical scale)
 * Half-window method handles blinds covering the top (see MEASUREMENT.md).
 */
(function () {
  "use strict";

  var DEFAULTS = {
    demoMode: false,            // true = don't POST to Shopify; show the payload instead
    uploadEndpoint: "",         // backend /upload URL; empty = photo storage unavailable
    // Reference is a sheet of A4 (210 × 297mm) taped flat to the window pane.
    reference: { label: "A4 sheet", widthMm: 210, heightMm: 297 },
    // Fabric colours offered. Colour is the only real product choice; size is derived from the
    // measured width and price comes from the ladder. The live config in photo-measure-widget.liquid
    // overrides this — these are inert fallback defaults, so they carry no variantId/price.
    products: [
      { colour: "natural", colourLabel: "Natural",    image: "" },
      { colour: "white",   colourLabel: "Milk White", image: "" }
    ],
    // Size from measured width (mm): Small if < smallMaxWidthMm, Standard if < standardMaxWidthMm,
    // wider than that = not sold online (shown as "contact us").
    smallMaxWidthMm: 675,
    standardMaxWidthMm: 1400,
    // Made-to-measure price ESTIMATE (£) — the firm price is confirmed in the quote:
    //   ( base + heightRatePerMetre[size] × (height + heightAddMm) + 2 × width × widthRatePerMetre ) × (1 + margin)
    // (rates are £ per metre; width/height in mm). The estimate is rounded to the nearest £ and
    // charged via a "price-ladder" product (one variant per whole pound, currently £44–£200) so the
    // cart shows a matching per-line estimate; the snippet fills priceVariants from that product.
    pricing: { base: 68, heightAddMm: 640, heightRatePerMetre: { small: 8.24, standard: 16.48 }, widthRatePerMetre: 4.24, margin: 0.15 },
    priceVariants: {}   // { "<wholePounds>": variantId } — built from the ladder product in the snippet
  };

  function mount(el) {
    var cfg = Object.assign({}, DEFAULTS, window.SASH_MEASURE_CONFIG || {});
    new Widget(el, cfg);
    // The widget is now the purchase path: flag the page so CSS hides the theme's native
    // quantity + add-to-cart buttons. Done only after a successful mount, so a script failure
    // leaves the native buttons in place as a fallback.
    document.documentElement.classList.add("sash-measure-active");
  }

  function Widget(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.state = {
      reference: cfg.reference,
      location: "",
      halfWindow: null,          // true / false
      file: null,
      img: null,                 // HTMLImageElement
      points: { ref: [], width: [], height: [] },
      manual: false,             // true = measurements typed in by hand (no photo)
      addedCount: 0,             // how many windows added to the basket this session
      lastAdd: null,             // { variant, props } of the most recent add
      result: null,              // { widthMm, heightMm }
      size: null,                // chosen size id
      colour: null,              // chosen colour id
      consent: false
    };
    this.renderFabric();
  }

  /* ---------- helpers ---------- */
  Widget.prototype.h = function (html) { this.root.innerHTML = html; };
  Widget.prototype.$ = function (sel) { return this.root.querySelector(sel); };
  Widget.prototype.$$ = function (sel) { return Array.prototype.slice.call(this.root.querySelectorAll(sel)); };

  function steps(active, total) {
    var li = "";
    for (var i = 0; i < total; i++) {
      var cls = i < active ? "is-done" : (i === active ? "is-active" : "");
      li += '<li class="' + cls + '"></li>';
    }
    return '<ul class="sw-steps">' + li + "</ul>";
  }

  function demoBanner(cfg) {
    return cfg.demoMode ? '<div class="sw-demo-banner">Preview mode — nothing is sent to a cart.</div>' : "";
  }

  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }

  // The widget adds via /cart/add.js, which bypasses the theme's own add-to-cart, so the header
  // cart bubble doesn't refresh on its own. We ask the Section Rendering API to re-render the
  // theme's `cart-icon-bubble` section and swap it into #cart-icon-bubble (matches Dawn's behaviour).
  function updateCartBubble(sections) {
    if (!sections || typeof document === "undefined") return;
    var html = sections["cart-icon-bubble"];
    var el = document.getElementById("cart-icon-bubble");
    if (!html || !el) return;
    try {
      var doc = new DOMParser().parseFromString(html, "text/html");
      var inner = doc.querySelector(".shopify-section");
      el.innerHTML = inner ? inner.innerHTML : html;
    } catch (e) { /* leave the bubble as-is on parse failure */ }
  }

  // Refresh the header cart count from the live cart. Covers themes whose count isn't the Dawn
  // bubble — e.g. the Fudge order page uses <span id="cart-count">. Belt-and-braces alongside
  // the Section Rendering bubble update above.
  function refreshCartCount() {
    if (typeof document === "undefined" || typeof fetch === "undefined") return;
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var els = document.querySelectorAll("#cart-count, .cart-count, [data-cart-count]");
        for (var i = 0; i < els.length; i++) els[i].textContent = cart.item_count;
      })
      .catch(function () { /* count refresh is best-effort */ });
  }

  function dist(a, b) { return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2)); }

  /* ---------- pure logic (no DOM — unit-tested, see tests/) ---------- */

  // Turn the tapped points into window dimensions (mm). The A4 corners give a horizontal and a
  // vertical mm-per-pixel scale; the sheet's orientation is auto-detected (longer pixel axis =
  // the longer mm side) so it's correct portrait OR landscape. Half-window doubles the height.
  function computeDimensions(points, reference, halfWindow) {
    var p = points.paper;                                   // tapped in order: TL, TR, BR, BL
    var horizPx = (dist(p[0], p[1]) + dist(p[3], p[2])) / 2; // avg of top & bottom edges
    var vertPx  = (dist(p[0], p[3]) + dist(p[1], p[2])) / 2; // avg of left & right edges
    var longMm = Math.max(reference.widthMm, reference.heightMm);   // 297 for A4
    var shortMm = Math.min(reference.widthMm, reference.heightMm);  // 210 for A4
    var horizMm = horizPx >= vertPx ? longMm : shortMm;
    var vertMm  = horizPx >= vertPx ? shortMm : longMm;
    var widthMm = dist(points.width[0], points.width[1]) * (horizMm / horizPx);
    var heightMm = dist(points.height[0], points.height[1]) * (vertMm / vertPx) * (halfWindow ? 2 : 1);
    return { widthMm: Math.round(widthMm), heightMm: Math.round(heightMm) };
  }

  // Product size from the measured width: "small" (< smallMaxWidthMm), "standard"
  // (< standardMaxWidthMm), or null when the window is too wide to sell online.
  function suggestSize(widthMm, smallMaxWidthMm, standardMaxWidthMm) {
    if (widthMm < smallMaxWidthMm) return "small";
    if (standardMaxWidthMm == null || widthMm < standardMaxWidthMm) return "standard";
    return null;
  }

  // Build the Shopify line-item properties for one window from the measurement state.
  // `extra` adds fields recorded when the variant itself is generic (e.g. the price-ladder variant):
  // Fabric, Size, Estimated price.
  function buildLineItemProps(state, name, photoUrl, extra) {
    var props = {
      "Window location": name || "Unnamed window",
      "Measured width (mm)": String(state.result.widthMm),
      "Measured height (mm)": String(state.result.heightMm),
      "Measure method": state.manual ? "Entered by hand" : (state.halfWindow ? "half-window ×2" : "full window")
    };
    // The manual path has no photo and no reference object by definition.
    if (!state.manual) {
      props["Reference object"] = state.reference.label + " (" + state.reference.widthMm + "×" + state.reference.heightMm + "mm)";
      if (photoUrl) props["Photo"] = photoUrl;
    }
    if (extra) {
      Object.keys(extra).forEach(function (k) { if (extra[k] != null && extra[k] !== "") props[k] = extra[k]; });
    }
    return props;
  }

  // Validate a manual width/height entry (mm). Returns an error message, or null if OK.
  function validateManual(width, height) {
    if (!(width > 0) || !(height > 0)) return "Please enter a width and a height in millimetres.";
    if (width > 5000 || height > 5000) return "That seems very large — please check the measurements are in millimetres, not centimetres.";
    return null;
  }

  // Map a (size, colour) choice to a configured product variant.
  function findVariant(products, size, colour) {
    return products.filter(function (p) { return p.size === size && p.colour === colour; })[0];
  }

  // Made-to-measure price ESTIMATE in £ (not rounded), INCLUDING the margin uplift:
  //   cost  = base + heightRatePerMetre[size] × (height + heightAddMm) + 2 × width × widthRatePerMetre
  //   price = cost × (1 + margin)
  // rates are £ per metre; width/height in mm. The caller rounds to the nearest £. Null if inputs/size missing.
  function calculatePrice(heightMm, widthMm, size, pricing) {
    if (!pricing || !pricing.heightRatePerMetre || !(heightMm > 0) || !(widthMm > 0)) return null;
    var hr = pricing.heightRatePerMetre[size];
    if (hr == null) hr = pricing.heightRatePerMetre.standard;   // fall back to standard rate
    if (hr == null) return null;
    var heightM = (heightMm + (pricing.heightAddMm || 0)) / 1000;
    var widthM = widthMm / 1000;
    var cost = (pricing.base || 0) + hr * heightM + 2 * widthM * (pricing.widthRatePerMetre || 0);
    return cost * (1 + (pricing.margin || 0));
  }

  // Map a calculated whole-pound price to its price-ladder variant id (undefined if out of range).
  function findPricedVariant(priceVariants, priceGBP) {
    if (!priceVariants || priceGBP == null) return undefined;
    return priceVariants[priceGBP] != null ? priceVariants[priceGBP] : priceVariants[String(priceGBP)];
  }

  function diagramDot(x, y, c) {
    return '<circle cx="' + x + '" cy="' + y + '" r="3.6" fill="' + c + '" stroke="#fff" stroke-width="1"/>';
  }
  // A small front-on sash-window diagram with the current step's click targets highlighted.
  // Shows the outer FIXED frame + sashes; measures to the inside of the fixed frame (the opening).
  function measureDiagram(stepKey, halfWindow) {
    var O = "#e07b39", G = "#2f7d3a", B = "#1f6f8b";
    // Sash window: a solid OUTER FIXED FRAME, the opening inside it, then the sashes
    // (four glass lights with their wood surrounds). Measure to the inside of the fixed frame.
    // Line-art 2-over-2 sash window (proportions from the sashshadingcompany guide): thin fixed
    // frame, two equal sliding sashes, tall panes. Bottom sash top edge = middle bar, full width.
    var base =
      '<rect x="10" y="128" width="100" height="8" class="d-sill"/>' +        // sill (sits below the frame, protrudes)
      '<rect x="16" y="8" width="88" height="120" class="d-frame"/>' +        // outer fixed frame (thin outline)
      '<rect x="22" y="14" width="76" height="54" class="d-sash"/>' +         // TOP sliding sash
      '<rect x="22" y="68" width="76" height="54" class="d-sash"/>' +         // BOTTOM sliding sash (top edge = middle bar)
      '<rect x="28" y="20" width="28" height="41" class="d-pane"/>' +         // top-left light
      '<rect x="64" y="20" width="28" height="41" class="d-pane"/>' +         // top-right light
      '<rect x="28" y="75" width="28" height="41" class="d-pane"/>' +         // bottom-left light
      '<rect x="64" y="75" width="28" height="41" class="d-pane"/>';          // bottom-right light
    var hl;
    if (stepKey === "paper") {
      hl = '<rect x="34" y="84" width="15" height="22" fill="none" stroke="' + O + '" stroke-width="2" stroke-dasharray="3 2"/>' +
        diagramDot(34, 84, O) + diagramDot(49, 84, O) + diagramDot(49, 106, O) + diagramDot(34, 106, O);
    } else if (stepKey === "width") {
      // inside of the fixed frame -> inside of the fixed frame (the opening width)
      hl = '<line x1="22" y1="96" x2="98" y2="96" stroke="' + G + '" stroke-width="2.5" stroke-dasharray="5 3"/>' +
        diagramDot(22, 96, G) + diagramDot(98, 96, G);
    } else if (halfWindow) {
      // CENTRE of the middle bar (the sash meeting line) -> bottom of the opening, doubled
      hl = '<line x1="60" y1="68" x2="60" y2="122" stroke="' + B + '" stroke-width="2.5" stroke-dasharray="5 3"/>' +
        diagramDot(60, 68, B) + diagramDot(60, 122, B) +
        '<text x="64" y="98" font-size="13" font-weight="700" fill="' + B + '">×2</text>';
    } else {
      // inside top of the fixed frame -> bottom of the opening
      hl = '<line x1="60" y1="14" x2="60" y2="122" stroke="' + B + '" stroke-width="2.5" stroke-dasharray="5 3"/>' +
        diagramDot(60, 14, B) + diagramDot(60, 122, B);
    }
    return '<svg viewBox="0 0 120 140" class="sw-diagram-svg" xmlns="http://www.w3.org/2000/svg">' + base + hl + "</svg>";
  }

  var TOTAL_STEPS = 6; // fabric, intro/details, half-window, capture, measure, result

  /* ---------- 0. choose fabric (shown first; later windows reuse it) ---------- */
  // Three expandable product-detail sections shown under the fabric swatches (native
  // <details> — no JS wiring needed). Same copy for every fabric/colour.
  function fabricDetails() {
    return (
      '<div class="sw-accordions">' +
        '<details class="sw-acc" open><summary>Fabric</summary><div class="sw-acc__body"><ul>' +
          "<li>100% natural fibres — biodegradable</li>" +
          "<li>100% European linen</li>" +
          "<li>Made in the UK</li>" +
          "<li>OEKO-TEX&reg; Standard 100 certified</li>" +
        "</ul></div></details>" +
        '<details class="sw-acc"><summary>Description</summary><div class="sw-acc__body"><p>' +
          "Feel cooler with these beautiful linen shades, thick enough to keep out the heat of the " +
          "sun but light enough to give that summery, airy feeling. Linen is durable, long-lasting " +
          "and flax requires less water and fewer chemicals to grow than many other fabric weaves." +
        "</p></div></details>" +
        '<details class="sw-acc"><summary>Wash &amp; Care</summary><div class="sw-acc__body"><ul>' +
          "<li>Wash on 30°C gentle cycle</li>" +
          "<li>Wash with similar colours</li>" +
          "<li>Dry flat</li>" +
          "<li>No need to iron — enjoy the crumples</li>" +
          "<li>Do not dry clean</li>" +
        "</ul></div></details>" +
      "</div>"
    );
  }

  // The distinct fabric/colour options, in config order (e.g. ["natural", "white"]).
  Widget.prototype.colours = function () {
    var cols = [];
    this.cfg.products.forEach(function (p) { if (cols.indexOf(p.colour) === -1) cols.push(p.colour); });
    return cols;
  };

  Widget.prototype.renderFabric = function () {
    var c = this.cfg, s = this.state, self = this;
    var cards = this.colours().map(function (col) {
      var sample = c.products.filter(function (p) { return p.colour === col; })[0];
      var img = sample.image
        ? '<span class="sw-fabric-img" style="background-image:url(' + esc(sample.image) + ')"></span>'
        : '<span class="sw-fabric-img sw-fabric-img--none"></span>';
      return '<button type="button" class="sw-fabric' + (s.colour === col ? " is-selected" : "") +
        '" data-colour="' + esc(col) + '">' + img +
        '<span class="sw-fabric-name">' + esc(sample.colourLabel) + " linen</span></button>";
    }).join("");
    this.h(
      '<div class="sw-card">' +
        demoBanner(c) +
        steps(0, TOTAL_STEPS) +
        '<p class="sw-eyebrow">Step 1 — choose your fabric</p>' +
        '<h3 class="sw-h">Which linen would you like?</h3>' +
        '<p class="sw-p">Pick your fabric first. You can then measure as many windows as you like — they’ll all use this fabric (you can change it any time).</p>' +
        '<div class="sw-fabrics">' + cards + "</div>" +
        fabricDetails() +
        '<div class="sw-btn-row"><span class="sw-spacer"></span>' +
          '<button class="sw-btn sw-btn-primary" id="sw-next"' + (s.colour ? "" : " disabled") + ">Continue</button>" +
        "</div>" +
      "</div>"
    );
    this.$$("[data-colour]").forEach(function (b) {
      b.addEventListener("click", function () {
        self.$$("[data-colour]").forEach(function (x) { x.classList.remove("is-selected"); });
        b.classList.add("is-selected");
        s.colour = b.getAttribute("data-colour");
        self.$("#sw-next").disabled = false;
      });
    });
    this.$("#sw-next").addEventListener("click", function () {
      if (s.colour) self.renderIntro();
    });
  };

  // A compact "Fabric: X · Change" bar shown on the steps after the fabric choice, so the
  // selection is always visible and changeable without losing the current window's progress.
  Widget.prototype.fabricBar = function () {
    return '<div class="sw-fabricbar"><span>Fabric: <strong>' +
      esc(this.labelFor("colour", this.state.colour)) + " linen</strong></span>" +
      '<button type="button" class="sw-linkbtn" id="sw-changefab">Change</button></div>';
  };
  Widget.prototype.wireFabricBar = function () {
    var self = this, b = this.$("#sw-changefab");
    if (b) b.addEventListener("click", function () { self.renderFabric(); });
  };

  /* ---------- 1. intro ---------- */
  Widget.prototype.renderIntro = function () {
    var c = this.cfg, s = this.state;
    this.h(
      '<div class="sw-card">' +
        demoBanner(c) +
        steps(1, TOTAL_STEPS) +
        this.fabricBar() +
        '<p class="sw-eyebrow">Measure without a tape measure</p>' +
        '<h3 class="sw-h">Take a photo, we\'ll do the measurements</h3>' +
        '<p class="sw-p">Tape a sheet of <strong>A4 paper</strong> flat onto the window glass, take a photo, and we’ll work out the size for you. The paper is a known size, so it tells us the scale — it’s maths!</p>' +
        '<div class="sw-field">' +
          '<label class="sw-label" for="sw-loc">Name this window (optional)</label>' +
          '<div class="sw-input-row">' +
            '<input id="sw-loc" class="sw-input" type="text" placeholder="e.g. Living room – left" />' +
            '<button type="button" class="sw-btn sw-btn-ghost sw-mic" id="sw-mic" title="Speak the name" aria-label="Speak the name">🎤</button>' +
          "</div>" +
          '<p class="sw-hint">Helpful if you’re measuring more than one window.</p>' +
        "</div>" +
        '<div class="sw-btn-row">' +
          '<button class="sw-btn sw-btn-ghost" id="sw-manual">Enter measurements by hand</button>' +
          '<span class="sw-spacer"></span>' +
          '<button class="sw-btn sw-btn-primary" id="sw-next">Take a photo</button>' +
        "</div>" +
      "</div>"
    );
    var self = this;
    this.$("#sw-loc").value = s.location;
    this.$("#sw-loc").addEventListener("input", function (e) { s.location = e.target.value; });
    this.setupMic();
    this.wireFabricBar();
    this.$("#sw-next").addEventListener("click", function () {
      s.location = self.$("#sw-loc").value;
      s.manual = false;
      self.renderHalfWindow();
    });
    this.$("#sw-manual").addEventListener("click", function () {
      s.location = self.$("#sw-loc").value;
      self.renderManual();
    });
  };

  Widget.prototype.setupMic = function (sel) {
    sel = sel || "#sw-loc";
    var btn = this.$("#sw-mic"); if (!btn) return;
    var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    // Voice dictation needs the Web Speech API AND a secure context (https / localhost).
    // It is unsupported on iOS Safari, and blocked on file:// previews. In those cases hide
    // the button — the phone keyboard's own mic still works in the text field.
    if (!SR || !window.isSecureContext) { btn.style.display = "none"; return; }
    var self = this, rec = new SR(), recording = false;
    rec.lang = "en-GB"; rec.interimResults = false; rec.maxAlternatives = 1;
    var stop = function () {
      recording = false; btn.classList.remove("is-recording"); btn.setAttribute("aria-pressed", "false");
    };
    rec.onresult = function (e) {
      var t = (e.results[0][0].transcript || "").trim();
      var inp = self.$(sel); if (inp) { inp.value = t; self.state.location = t; }
    };
    rec.onerror = stop;
    rec.onend = stop;
    btn.addEventListener("click", function () {
      if (recording) { try { rec.stop(); } catch (e) {} return; }
      try { rec.start(); recording = true; btn.classList.add("is-recording"); btn.setAttribute("aria-pressed", "true"); }
      catch (e) { stop(); }
    });
  };

  /* ---------- 1b. manual entry (alternative to the photo flow) ---------- */
  // The "How to Measure" guide diagram, shown inside the manual step so it's available on mobile
  // (where the desktop-only side guide panel is hidden). Uses the guideImage URL passed from the
  // snippet; renders nothing if none is configured. CSS hides it on desktop to avoid duplicating
  // the side panel. Open by default so it's immediately visible.
  function manualGuide(cfg) {
    if (!cfg.guideImage) return "";
    return (
      '<details class="sw-manual-guide" open>' +
        "<summary>How to measure</summary>" +
        '<div class="sw-manual-guide__body">' +
          '<p class="sw-manual-guide__note">N.B. The height and width measurements are the moving ' +
          "sashes and not the whole window frame.</p>" +
          '<img src="' + esc(cfg.guideImage) + '" alt="How to measure your sash window" loading="lazy" />' +
        "</div>" +
      "</details>"
    );
  }

  Widget.prototype.renderManual = function () {
    var c = this.cfg, s = this.state;
    this.h(
      '<div class="sw-card">' +
        demoBanner(c) +
        steps(4, TOTAL_STEPS) +
        this.fabricBar() +
        '<p class="sw-eyebrow">Enter measurements by hand</p>' +
        '<h3 class="sw-h">Type your window size</h3>' +
        '<p class="sw-p">Already measured with a tape? Enter the <strong>opening</strong> size below — the inside of the frame, in millimetres.</p>' +
        manualGuide(c) +
        '<div class="sw-field">' +
          '<label class="sw-label" for="sw-mloc">Name this window (optional)</label>' +
          '<div class="sw-input-row">' +
            '<input id="sw-mloc" class="sw-input" type="text" placeholder="e.g. Living room – left" />' +
            '<button type="button" class="sw-btn sw-btn-ghost sw-mic" id="sw-mic" title="Speak the name" aria-label="Speak the name">🎤</button>' +
          "</div>" +
        "</div>" +
        '<div class="sw-input-row" style="gap:0.8rem">' +
          '<div class="sw-field" style="flex:1;margin:0">' +
            '<label class="sw-label" for="sw-mw">Width (mm)</label>' +
            '<input id="sw-mw" class="sw-input" type="number" inputmode="numeric" min="1" placeholder="e.g. 760" />' +
          "</div>" +
          '<div class="sw-field" style="flex:1;margin:0">' +
            '<label class="sw-label" for="sw-mh">Height (mm)</label>' +
            '<input id="sw-mh" class="sw-input" type="number" inputmode="numeric" min="1" placeholder="e.g. 1320" />' +
          "</div>" +
        "</div>" +
        '<p class="sw-hint">Measure the opening width and height — inside edge of the frame to inside edge.</p>' +
        '<p class="sw-error" id="sw-err" style="display:none"></p>' +
        '<div class="sw-btn-row">' +
          '<button class="sw-btn sw-btn-ghost" id="sw-back">Back</button><span class="sw-spacer"></span>' +
          '<button class="sw-btn sw-btn-primary" id="sw-next">See your price</button>' +
        "</div>" +
      "</div>"
    );
    var self = this;
    var loc = this.$("#sw-mloc"); loc.value = s.location;
    loc.addEventListener("input", function (e) { s.location = e.target.value; });
    this.setupMic("#sw-mloc");
    this.wireFabricBar();
    if (s.manual && s.result) { this.$("#sw-mw").value = s.result.widthMm; this.$("#sw-mh").value = s.result.heightMm; }
    this.$("#sw-back").addEventListener("click", function () { self.renderIntro(); });
    this.$("#sw-next").addEventListener("click", function () {
      var err = self.$("#sw-err"); err.style.display = "none";
      var w = parseInt(self.$("#sw-mw").value, 10), h = parseInt(self.$("#sw-mh").value, 10);
      var problem = validateManual(w, h);
      if (problem) { err.textContent = problem; err.style.display = "block"; return; }
      s.location = loc.value;
      s.manual = true; s.halfWindow = null; s.file = null; s.img = null;
      s.result = { widthMm: w, heightMm: h };
      s.size = suggestSize(w, self.cfg.smallMaxWidthMm, self.cfg.standardMaxWidthMm);
      var firstColour = self.cfg.products[0];
      s.colour = s.colour || (firstColour ? firstColour.colour : null);
      self.renderResult();
    });
  };

  /* ---------- 2. half-window confirmation ---------- */
  Widget.prototype.renderHalfWindow = function () {
    var c = this.cfg, s = this.state;
    this.h(
      '<div class="sw-card">' +
        demoBanner(c) +
        steps(2, TOTAL_STEPS) +
        '<p class="sw-eyebrow">Quick question</p>' +
        '<h3 class="sw-h">Is the middle bar halfway up your window?</h3>' +
        '<p class="sw-p">On many sash windows the top and bottom halves are equal. If yours is, we can work out the full height even when an internal blind or curtain hides the top — just measure the bottom half.</p>' +
        '<div class="sw-choices">' +
          '<button class="sw-choice" data-half="yes"><strong>Yes — it’s halfway</strong><span>Top &amp; bottom look equal. An internal blind or curtain on top is fine.</span></button>' +
          '<button class="sw-choice" data-half="no"><strong>No / not sure</strong><span>We’ll need the whole window visible — raise any internal blind or curtain.</span></button>' +
        "</div>" +
        '<p class="sw-hint">Choosing “Yes” confirms the middle bar is central. We’ll work out the full height from the bottom half, so please make sure it really does look halfway up.</p>' +
        '<div class="sw-btn-row">' +
          '<button class="sw-btn sw-btn-ghost" id="sw-back">Back</button><span class="sw-spacer"></span>' +
          '<button class="sw-btn sw-btn-primary" id="sw-next" disabled>Continue</button>' +
        "</div>" +
      "</div>"
    );
    var self = this;
    this.$$(".sw-choice").forEach(function (b) {
      b.addEventListener("click", function () {
        self.$$(".sw-choice").forEach(function (x) { x.classList.remove("is-selected"); });
        b.classList.add("is-selected");
        s.halfWindow = b.getAttribute("data-half") === "yes";
        self.$("#sw-next").disabled = false;
      });
    });
    if (s.halfWindow !== null) {
      var sel = this.$('.sw-choice[data-half="' + (s.halfWindow ? "yes" : "no") + '"]');
      if (sel) { sel.classList.add("is-selected"); this.$("#sw-next").disabled = false; }
    }
    this.$("#sw-back").addEventListener("click", function () { self.renderIntro(); });
    this.$("#sw-next").addEventListener("click", function () { self.renderCapture(); });
  };

  /* ---------- 3. capture ---------- */
  Widget.prototype.renderCapture = function () {
    var c = this.cfg, s = this.state;
    var what = s.halfWindow
      ? "Tape the A4 sheet on the <strong>lower pane</strong>. Keep the sheet, the middle bar and the bottom of the sill clearly visible — your existing internal blind or curtain can stay where it is."
      : "Keep the <strong>A4 sheet and the whole window frame</strong> visible — please raise any existing internal blind or curtain (these are your own window coverings, not our shade).";
    this.h(
      '<div class="sw-card">' +
        demoBanner(c) +
        steps(3, TOTAL_STEPS) +
        '<p class="sw-eyebrow">Take the photo</p>' +
        '<h3 class="sw-h">Photograph your window</h3>' +
        '<p class="sw-p">Tape an A4 sheet flat on the glass (portrait, tall way up), then stand square-on with your phone parallel to the window. ' + what + "</p>" +
        '<div class="sw-capture-zone">' +
          '<input id="sw-file" type="file" accept="image/*" capture="environment" hidden />' +
          '<button class="sw-btn sw-btn-primary" id="sw-take">📸 Take / choose photo</button>' +
          '<p class="sw-hint" style="margin-top:0.8rem">Your photo stays on your device unless you choose to save it later.</p>' +
        "</div>" +
        '<div class="sw-btn-row">' +
          '<button class="sw-btn sw-btn-ghost" id="sw-back">Back</button>' +
        "</div>" +
      "</div>"
    );
    var self = this;
    var fileInput = this.$("#sw-file");
    this.$("#sw-take").addEventListener("click", function () { fileInput.click(); });
    fileInput.addEventListener("change", function (e) {
      var f = e.target.files && e.target.files[0]; if (!f) return;
      s.file = f;
      var img = new Image();
      img.onload = function () { s.img = img; self.startMeasure(); };
      img.onerror = function () { alert("Sorry, we couldn’t read that image. Please try another photo."); };
      img.src = URL.createObjectURL(f);
    });
    this.$("#sw-back").addEventListener("click", function () { self.renderHalfWindow(); });
  };

  /* ---------- 4. measure (tap based) ---------- */
  Widget.prototype.startMeasure = function () {
    var s = this.state;
    s.points = { paper: [], width: [], height: [] };
    this.measureSeq = [
      { key: "paper", need: 4, title: "Mark the A4 sheet",
        summary: "Tap the four corners of the A4 sheet — then drag any point to fine-tune it.",
        labels: ["top-left corner", "top-right corner", "bottom-right corner", "bottom-left corner"] },
      { key: "width", need: 2, title: "Measure the width",
        summary: "Tap the inside of the frame on the left and right — the width of the opening (see diagram).",
        labels: ["inside of the frame — LEFT", "inside of the frame — RIGHT"] },
      { key: "height", need: 2,
        title: s.halfWindow ? "Measure the bottom half" : "Measure the height",
        summary: s.halfWindow
          ? "Tap the CENTRE of the middle bar, then the bottom of the opening — we’ll double it."
          : "Tap the inside top of the frame, then the bottom of the opening (top of the bottom frame).",
        labels: s.halfWindow
          ? ["centre of the middle bar", "bottom of the opening (top of the bottom frame)"]
          : ["inside top of the frame", "bottom of the opening (top of the bottom frame)"] }
    ];
    this.measureIdx = 0;
    this.renderMeasure();
  };

  Widget.prototype.renderMeasure = function () {
    var c = this.cfg, s = this.state, step = this.measureSeq[this.measureIdx];
    var isLast = this.measureIdx === this.measureSeq.length - 1;
    var nextLabel = isLast ? "See your price" : "Next";
    this.h(
      '<div class="sw-card">' +
        demoBanner(c) +
        steps(4, TOTAL_STEPS) +
        '<div class="sw-measure-head">' +
          '<button type="button" class="sw-diagram" id="sw-diagram" aria-label="Enlarge the guide diagram">' +
            measureDiagram(step.key, s.halfWindow) + '<span class="sw-diagram-cap">Tap to enlarge</span>' +
          "</button>" +
          '<div class="sw-measure-headtext">' +
            '<span class="sw-ov-step">Step ' + (this.measureIdx + 1) + ' of 3</span>' +
            '<strong>' + esc(step.title) + "</strong>" +
            '<span class="sw-head-sub">' + esc(step.summary) + "</span>" +
          "</div>" +
        "</div>" +
        '<div class="sw-canvas-wrap">' +
          '<canvas class="sw-canvas" id="sw-canvas"></canvas>' +
          '<canvas class="sw-loupe" id="sw-loupe" width="128" height="128"></canvas>' +
          // floating prompt that follows your last point: next instruction, then Redo/Next
          '<div class="sw-float" id="sw-float" hidden></div>' +
        "</div>" +
        '<div class="sw-measure-foot">' +
          '<button class="sw-btn sw-btn-ghost sw-btn-sm" id="sw-reshoot">New photo</button>' +
          '<span class="sw-spacer"></span>' +
          '<span class="sw-hint" id="sw-tapinfo"></span>' +
        "</div>" +
        '<p class="sw-hint sw-tip">Press and drag for a magnifier, then lift to place. Drag any placed point to nudge it.</p>' +
      "</div>"
    );
    var self = this;
    var canvas = this.$("#sw-canvas");
    var loupe = this.$("#sw-loupe");
    var floatEl = this.$("#sw-float");
    var wrap = canvas.parentNode;
    this.fitCanvas(canvas);
    this.drawCanvas();

    var palette = { paper: "#e07b39", width: "#365f2e", height: "#1f6f8b" };
    var ZOOM = 2.6, placing = false, candidate = null, mode = null, dragIdx = -1;
    var HIT = Math.max(22, canvas.width * 0.06);   // bitmap-px radius to grab a placed point

    function toBitmap(e) {
      var rect = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) * (canvas.width / rect.width),
        y: (e.clientY - rect.top) * (canvas.height / rect.height)
      };
    }
    // Magnifier: copies the region around the point (clean image + committed markers) at ZOOMx,
    // with a crosshair at the exact spot the point will land.
    function drawLoupe(bx, by, clientX, clientY, isTouch) {
      var lctx = loupe.getContext("2d"), lw = loupe.width, lh = loupe.height;
      var srcW = lw / ZOOM, srcH = lh / ZOOM, sx = bx - srcW / 2, sy = by - srcH / 2;
      lctx.fillStyle = "#eef0ea"; lctx.fillRect(0, 0, lw, lh);
      var ix = Math.max(sx, 0), iy = Math.max(sy, 0);
      var ix2 = Math.min(sx + srcW, canvas.width), iy2 = Math.min(sy + srcH, canvas.height);
      if (ix2 > ix && iy2 > iy) {
        lctx.drawImage(canvas, ix, iy, ix2 - ix, iy2 - iy,
          (ix - sx) * ZOOM, (iy - sy) * ZOOM, (ix2 - ix) * ZOOM, (iy2 - iy) * ZOOM);
      }
      lctx.strokeStyle = "rgba(54,95,46,0.9)"; lctx.lineWidth = 1;
      lctx.beginPath(); lctx.moveTo(lw / 2, 0); lctx.lineTo(lw / 2, lh);
      lctx.moveTo(0, lh / 2); lctx.lineTo(lw, lh / 2); lctx.stroke();
      lctx.fillStyle = "rgba(224,123,57,0.95)";
      lctx.beginPath(); lctx.arc(lw / 2, lh / 2, 3, 0, Math.PI * 2); lctx.fill();
      // Position the loupe. On touch/pen the finger covers the point, so lift the loupe (and its
      // crosshair) above the finger — flipping below only if there isn't room. With a mouse, keep it
      // centred on the cursor so the zoom sits right behind the crosshair.
      var wr = wrap.getBoundingClientRect();
      var px = clientX - wr.left, py = clientY - wr.top;
      var left = px - lw / 2, top;
      if (isTouch) {
        var gap = 28;
        top = py - gap - lh;
        if (top < 0) top = py + gap;            // not enough room above → drop below the finger
      } else {
        top = py - lh / 2;
      }
      left = Math.max(0, Math.min(left, wrap.clientWidth - lw));
      loupe.style.left = left + "px";
      loupe.style.top = top + "px";
      loupe.style.display = "block";
    }
    function hideLoupe() { loupe.style.display = "none"; }
    function nearestIdx(pts, p) {
      var best = -1, bestD = HIT;
      for (var i = 0; i < pts.length; i++) { var d = dist(pts[i], p); if (d <= bestD) { bestD = d; best = i; } }
      return best;
    }
    function preview(e) {
      candidate = toBitmap(e);
      var pts = s.points[step.key];
      if (mode === "drag" && dragIdx >= 0) pts[dragIdx] = { x: candidate.x, y: candidate.y };
      self.drawCanvas();                                  // clean image + committed markers (+ axis guide)
      var ctx = canvas.getContext("2d");
      // While placing the 2nd point of a dimension, draw the live measurement line so you can
      // line it up against the faint true-axis guide (level for width, plumb for height).
      if (mode === "place" && (step.key === "width" || step.key === "height") && pts.length === 1) {
        ctx.strokeStyle = palette[step.key]; ctx.lineWidth = 2; ctx.setLineDash([]);
        ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y); ctx.lineTo(candidate.x, candidate.y); ctx.stroke();
      }
      drawLoupe(candidate.x, candidate.y, e.clientX, e.clientY, e.pointerType !== "mouse"); // touch/pen → lift above finger
      ctx.strokeStyle = "rgba(224,123,57,0.95)"; ctx.lineWidth = 2;  // transient crosshair
      ctx.beginPath(); ctx.arc(candidate.x, candidate.y, 9, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(candidate.x - 14, candidate.y); ctx.lineTo(candidate.x + 14, candidate.y);
      ctx.moveTo(candidate.x, candidate.y - 14); ctx.lineTo(candidate.x, candidate.y + 14); ctx.stroke();
    }
    function commit() {
      var pts = s.points[step.key];
      if (mode === "drag") { mode = null; dragIdx = -1; candidate = null; self.drawCanvas(); afterChange(); return; }
      if (!candidate || pts.length >= step.need) return;
      pts.push({ x: candidate.x, y: candidate.y });
      candidate = null;
      if (pts.length >= step.need) hideLoupe();
      self.drawCanvas(); afterChange();
    }
    canvas.addEventListener("pointerdown", function (e) {
      var pts = s.points[step.key];
      e.preventDefault(); floatEl.hidden = true;
      try { canvas.setPointerCapture(e.pointerId); } catch (x) {}
      if (pts.length >= step.need) {
        // all points placed — grab the nearest one to amend it (drag), else ignore
        var hit = nearestIdx(pts, toBitmap(e));
        if (hit === -1) { floatEl.hidden = false; return; }
        mode = "drag"; dragIdx = hit; placing = true; preview(e);
      } else {
        mode = "place"; placing = true; preview(e);
      }
    });
    canvas.addEventListener("pointermove", function (e) {
      if (placing) { e.preventDefault(); preview(e); }
      else if (e.pointerType === "mouse") {
        if (s.points[step.key].length < step.need) { mode = "place"; preview(e); } else hideLoupe();
      }
    });
    canvas.addEventListener("pointerup", function (e) {
      if (!placing) return;
      placing = false; commit();
      if (e.pointerType !== "mouse") hideLoupe();
    });
    canvas.addEventListener("pointercancel", function () { placing = false; mode = null; dragIdx = -1; hideLoupe(); self.drawCanvas(); });
    canvas.addEventListener("pointerleave", function (e) {
      if (!placing && e.pointerType === "mouse") { hideLoupe(); self.drawCanvas(); }
    });
    this.$("#sw-reshoot").addEventListener("click", function () { self.renderCapture(); });
    this.$("#sw-diagram").addEventListener("click", function () { self.showDiagramModal(step); });

    function onRedo() {
      s.points[step.key] = []; candidate = null; mode = null; dragIdx = -1;
      hideLoupe(); self.drawCanvas(); afterChange();
    }
    function onNext() {
      if (self.measureIdx < self.measureSeq.length - 1) { self.measureIdx++; self.renderMeasure(); }
      else { self.compute(); self.renderResult(); }
    }
    function pointToDisplay(p) {
      var rect = canvas.getBoundingClientRect();
      return { x: p.x * (rect.width / canvas.width), y: p.y * (rect.height / canvas.height) };
    }
    function positionFloat(anchor) {
      var fw = floatEl.offsetWidth, fh = floatEl.offsetHeight;
      var x = anchor.x - fw / 2, y = anchor.y + 18;          // below the point by default
      if (y + fh > wrap.clientHeight - 4) y = anchor.y - fh - 18; // flip above if no room
      x = Math.max(4, Math.min(x, wrap.clientWidth - fw - 4));
      y = Math.max(4, Math.min(y, wrap.clientHeight - fh - 4));
      floatEl.style.left = x + "px"; floatEl.style.top = y + "px";
    }
    // The prompt follows your last point: next instruction while placing, Redo/Next when done.
    function updateFloat() {
      if (placing) { floatEl.hidden = true; return; }
      var pts = s.points[step.key];
      if (pts.length >= step.need) {
        var msg = "Looks right?";
        if (step.key === "paper") msg = "Drag a corner to nudge it";
        else if (step.key === "width") msg = "Level across the opening?";
        else if (step.key === "height") msg = "Straight up and down?";
        floatEl.className = "sw-float is-actions"; floatEl.hidden = false;
        floatEl.innerHTML =
          '<span class="sw-float-msg">' + esc(msg) + "</span>" +
          '<button class="sw-btn sw-btn-ghost sw-btn-sm" id="sw-redo">Redo</button>' +
          '<button class="sw-btn sw-btn-primary sw-btn-sm" id="sw-next">' + nextLabel + "</button>";
        positionFloat(pointToDisplay(pts[pts.length - 1]));
        floatEl.querySelector("#sw-redo").addEventListener("click", onRedo);
        floatEl.querySelector("#sw-next").addEventListener("click", onNext);
      } else if (pts.length === 0) {
        floatEl.className = "sw-float is-hint"; floatEl.hidden = false;
        floatEl.innerHTML = '<span class="sw-float-msg">Tap the ' + esc(step.labels[0]) + "</span>";
        positionFloat({ x: wrap.clientWidth / 2, y: 66 });
      } else {
        floatEl.className = "sw-float is-hint"; floatEl.hidden = false;
        floatEl.innerHTML = '<span class="sw-float-msg">Next: the ' + esc(step.labels[pts.length]) + "</span>";
        positionFloat(pointToDisplay(pts[pts.length - 1]));
      }
    }
    function afterChange() { self.updateMeasureUi(); updateFloat(); }

    afterChange();
  };

  // Enlarged version of the current step's guide diagram, so the targets are easy to read.
  Widget.prototype.showDiagramModal = function (step) {
    var s = this.state;
    var existing = this.$("#sw-diagram-modal");
    if (existing) { existing.parentNode.removeChild(existing); }
    var modal = document.createElement("div");
    modal.id = "sw-diagram-modal"; modal.className = "sw-modal";
    modal.innerHTML =
      '<div class="sw-modal-card" role="dialog" aria-modal="true" aria-label="Measurement guide">' +
        '<p class="sw-eyebrow">' + esc(step.title) + "</p>" +
        '<div class="sw-modal-diagram">' + measureDiagram(step.key, s.halfWindow) + "</div>" +
        '<p class="sw-p">' + esc(step.summary) + "</p>" +
        '<div class="sw-btn-row"><span class="sw-spacer"></span>' +
          '<button class="sw-btn sw-btn-primary" id="sw-modal-close">Got it</button>' +
        "</div>" +
      "</div>";
    this.root.appendChild(modal);
    var close = function () { if (modal.parentNode) modal.parentNode.removeChild(modal); };
    modal.addEventListener("click", function (e) { if (e.target === modal) close(); });
    this.$("#sw-modal-close").addEventListener("click", close);
  };

  Widget.prototype.fitCanvas = function (canvas) {
    var img = this.state.img;
    // Use the canvas's actual rendered width so bitmap pixels == CSS pixels (prevents tap offset).
    var cssW = canvas.clientWidth || this.root.clientWidth || 360;
    var ratio = img.naturalHeight / img.naturalWidth;
    canvas.width = Math.round(cssW);
    canvas.height = Math.round(cssW * ratio);
    this._canvas = canvas;
  };

  Widget.prototype.drawCanvas = function () {
    var canvas = this._canvas, ctx = canvas.getContext("2d"), s = this.state;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(s.img, 0, 0, canvas.width, canvas.height);
    var step = this.measureSeq[this.measureIdx];
    // distinct, high-contrast marker hues for visibility on a photo
    var palette = { paper: "#e07b39", width: "#365f2e", height: "#1f6f8b" };
    Object.keys(s.points).forEach(function (key) {
      var pts = s.points[key]; if (!pts.length) return;
      var col = palette[key] || "#557366";
      // connect points (polygon for the 4-corner paper, line otherwise)
      if (pts.length >= 2) {
        ctx.strokeStyle = col; ctx.lineWidth = 2; ctx.setLineDash([6, 4]);
        ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
        for (var i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        if (key === "paper" && pts.length === 4) ctx.closePath();
        ctx.stroke(); ctx.setLineDash([]);
      }
      pts.forEach(function (p) {
        var active = (key === step.key);
        var ring = active ? col : "rgba(85,115,102,0.75)";
        // Hollow ring (the photo shows through the centre) + white halo for contrast on any photo,
        // and a tiny centre dot marking the exact point — so you can see precisely where it sits.
        ctx.beginPath(); ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255,255,255,0.9)"; ctx.lineWidth = 3.5; ctx.stroke();
        ctx.beginPath(); ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
        ctx.strokeStyle = ring; ctx.lineWidth = 2; ctx.stroke();
        ctx.beginPath(); ctx.arc(p.x, p.y, 1.6, 0, Math.PI * 2);
        ctx.fillStyle = ring; ctx.fill();
      });
    });
    // Width/height: a faint true-axis guide through the first point — line your second point up
    // with it to keep the measurement level (width) or plumb (height).
    if ((step.key === "width" || step.key === "height") && s.points[step.key].length >= 1) {
      var a = s.points[step.key][0], gcol = palette[step.key] || "#365f2e";
      ctx.save();
      ctx.strokeStyle = gcol; ctx.globalAlpha = 0.35; ctx.lineWidth = 1.5; ctx.setLineDash([4, 4]);
      ctx.beginPath();
      if (step.key === "width") { ctx.moveTo(0, a.y); ctx.lineTo(canvas.width, a.y); }
      else { ctx.moveTo(a.x, 0); ctx.lineTo(a.x, canvas.height); }
      ctx.stroke(); ctx.restore();
    }
  };

  Widget.prototype.updateMeasureUi = function () {
    var s = this.state, step = this.measureSeq[this.measureIdx];
    var pts = s.points[step.key];
    var info = this.$("#sw-tapinfo");
    var done = pts.length >= step.need;
    if (info) info.textContent = done ? "All points placed" : (pts.length + " of " + step.need + " points placed");
  };

  Widget.prototype.compute = function () {
    var s = this.state;
    s.result = computeDimensions(s.points, s.reference, s.halfWindow);
    s.size = suggestSize(s.result.widthMm, this.cfg.smallMaxWidthMm, this.cfg.standardMaxWidthMm);
    var firstColour = this.cfg.products[0];
    s.colour = s.colour || (firstColour ? firstColour.colour : null);
  };

  /* ---------- 5. result + product + consent ---------- */
  Widget.prototype.renderResult = function () {
    var c = this.cfg, s = this.state, r = s.result, calc = this.usingCalcPricing();
    // Fabric was chosen up front; size is determined from the measurements and not changeable here.
    var sizeLabel = s.size ? this.labelFor("size", s.size) : "Too wide for online ordering";
    var fabricLabel = this.labelFor("colour", s.colour);
    var storageNote = c.uploadEndpoint
      ? "If you tick this, your photo is saved securely with your order so we can double-check the fit."
      : "Photo saving isn’t switched on yet — only your measurements will be sent for now.";
    var methodHint = s.manual
      ? "You entered these by hand."
      : (s.halfWindow ? "Full height calculated by doubling the bottom half, based on your confirmation that the middle bar is central." : "Measured across the full window opening.");
    // No photo on the manual path, so the consent control only appears after a photo measure.
    var consentBlock = s.manual ? "" :
      '<div class="sw-consent">' +
        '<input type="checkbox" id="sw-consent"' + (c.uploadEndpoint ? "" : " disabled") + ' />' +
        '<div><label for="sw-consent">Save my photo with the order</label>' +
        '<p class="sw-hint">' + storageNote + "</p></div>" +
      "</div>";

    this.h(
      '<div class="sw-card">' +
        demoBanner(c) +
        steps(5, TOTAL_STEPS) +
        '<p class="sw-eyebrow">' + (s.location ? esc(s.location) : "Your window") + "</p>" +
        '<h3 class="sw-h">Here’s your size</h3>' +
        '<div class="sw-readout">' +
          '<div class="sw-stat"><b>' + r.widthMm + '</b><span>Width (mm)</span></div>' +
          '<div class="sw-stat"><b>' + r.heightMm + '</b><span>Height (mm)</span></div>' +
        "</div>" +
        '<p class="sw-hint">' + methodHint + " Please double-check before ordering.</p>" +
        '<div class="sw-spec" style="margin-top:1rem">' +
          '<div class="sw-spec-row"><span>Fabric</span><strong>' + esc(fabricLabel) + " linen</strong></div>" +
          '<div class="sw-spec-row"><span>Size</span><strong>' + esc(sizeLabel) + "</strong></div>" +
        "</div>" +
        '<p class="sw-hint">Size is set automatically from your measurements.</p>' +
        '<div class="sw-price-row"><span class="sw-price-label">Estimated price</span><b class="sw-price-value" id="sw-price">—</b></div>' +
        '<p class="sw-hint" id="sw-price-note" style="display:none"></p>' +
        consentBlock +
        '<p class="sw-error" id="sw-err" style="display:none"></p>' +
        '<div class="sw-btn-row">' +
          '<button class="sw-btn sw-btn-ghost" id="sw-back">Back</button><span class="sw-spacer"></span>' +
          '<button class="sw-btn sw-btn-primary" id="sw-add">Add to basket</button>' +
        "</div>" +
        '<div id="sw-payload"></div>' +
      "</div>"
    );
    var self = this;
    function updatePrice() {
      var el = self.$("#sw-price"), note = self.$("#sw-price-note"), addBtn = self.$("#sw-add");
      if (calc) {
        // Estimated price from width + height + size, rounded to the nearest £ and charged via the
        // matching price-ladder variant (an estimate; the firm price is confirmed in the quote).
        var raw = (s.size == null) ? null : calculatePrice(s.result.heightMm, s.result.widthMm, s.size, c.pricing);
        var gbp = (raw == null) ? null : Math.round(raw);
        var vid = findPricedVariant(c.priceVariants, gbp);
        s.calcPrice = gbp; s.pricedVariantId = vid;
        if (el) el.textContent = (gbp != null) ? "£" + gbp : "—";
        var inRange = !!vid;
        if (note) {
          note.style.display = inRange ? "none" : "block";
          if (!inRange) note.textContent = (s.size == null)
            ? "This window is wider than we currently sell online — please email cool@sashshading.com and we’ll quote you."
            : "This size is larger than we sell online — please email cool@sashshading.com and we’ll quote you.";
        }
        if (addBtn) addBtn.disabled = !inRange;
      } else {
        var v = self.findVariant();
        if (el) el.textContent = (v && v.price) ? v.price : "—";
        if (note) note.style.display = "none";
      }
    }
    updatePrice();
    var consentBox = this.$("#sw-consent");
    if (consentBox) consentBox.addEventListener("change", function (e) { s.consent = e.target.checked; });
    this.$("#sw-back").addEventListener("click", function () {
      if (s.manual) { self.renderManual(); }
      else { self.measureIdx = self.measureSeq.length - 1; self.renderMeasure(); }
    });
    this.$("#sw-add").addEventListener("click", function () { self.submit(); });
  };

  Widget.prototype.findVariant = function () {
    return findVariant(this.cfg.products, this.state.size, this.state.colour);
  };

  // Calculated pricing is active when a price-ladder map has been provided (built from the
  // made-to-measure product in the snippet). Otherwise the widget uses the flat per-size variants.
  Widget.prototype.usingCalcPricing = function () {
    var pv = this.cfg.priceVariants;
    return !!(this.cfg.pricing && pv && Object.keys(pv).length);
  };
  // Human label for a size/colour value, from the configured products (falls back to Capitalised).
  Widget.prototype.labelFor = function (key, val) {
    var prod = this.cfg.products.filter(function (p) { return p[key] === val; })[0];
    if (prod && prod[key + "Label"]) return prod[key + "Label"];
    return val ? val.charAt(0).toUpperCase() + val.slice(1) : val;
  };

  // Build the line-item properties for one window from the current measurement state.
  Widget.prototype.buildProps = function (name, photoUrl, extra) {
    return buildLineItemProps(this.state, name, photoUrl, extra);
  };

  // Add one line to the cart (or stash it in demo), then show the "what next?" screen.
  Widget.prototype.finishAdd = function (variant, props, btn, showErr) {
    var self = this, c = this.cfg, s = this.state;
    var done = function () { s.addedCount++; s.lastAdd = { variant: variant, props: props }; self.renderAdded(); };
    if (c.demoMode) { done(); return; }
    // Post form-encoded (NOT application/json): this storefront rejects JSON on /cart/add.js with a
    // 400, which silently dropped the variant + properties. Form-encoded is universally accepted.
    // `properties[Key]=Value` attaches the line-item properties; `sections` re-renders the cart bubble.
    var form = new URLSearchParams();
    form.append("id", variant.variantId);
    form.append("quantity", "1");
    Object.keys(props).forEach(function (k) { form.append("properties[" + k + "]", props[k]); });
    form.append("sections", "cart-icon-bubble");
    form.append("sections_url", window.location.pathname);
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
      body: form.toString()
    }).then(function (res) {
      if (!res.ok) throw new Error("cart");
      return res.json();
    }).then(function (data) {
      updateCartBubble(data && data.sections);   // Dawn header bubble
      refreshCartCount();                          // Fudge header (#cart-count) + generic counts
      done();
    }).catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = "Add to basket"; }
        if (showErr) showErr("Sorry, we couldn’t add that to your basket. Please try again.");
      });
  };

  Widget.prototype.submit = function () {
    var self = this, c = this.cfg, s = this.state;
    var err = this.$("#sw-err");
    var showErr = function (m) { err.textContent = m; err.style.display = "block"; };
    err.style.display = "none";

    var variant, extra = null;
    if (this.usingCalcPricing()) {
      // The price-ladder variant carries the calculated price; fabric/size ride as properties.
      if (!s.pricedVariantId) { showErr("This size is larger than we sell online — please email cool@sashshading.com for a quote."); return; }
      variant = { variantId: s.pricedVariantId };
      extra = {
        "Fabric": this.labelFor("colour", s.colour),
        "Size": this.labelFor("size", s.size),
        "Estimated price": "£" + s.calcPrice
      };
    } else {
      variant = this.findVariant();
      if (!variant) { showErr("That size/colour combination isn’t available. Please pick another."); return; }
    }

    var addBtn = this.$("#sw-add");
    addBtn.disabled = true; addBtn.textContent = "Working…";

    var add = function (photoUrl) { self.finishAdd(variant, self.buildProps(s.location, photoUrl, extra), addBtn, showErr); };

    // consent-gated upload (photo path only)
    if (!s.manual && s.consent && c.uploadEndpoint && s.file) {
      var fd = new FormData(); fd.append("photo", s.file);
      fetch(c.uploadEndpoint, { method: "POST", body: fd })
        .then(function (r) { if (!r.ok) throw new Error("upload"); return r.json(); })
        .then(function (data) { add(data && data.url); })
        .catch(function () {
          addBtn.disabled = false; addBtn.textContent = "Add to basket";
          showErr("We couldn’t save your photo just now. Untick “Save my photo” to continue with measurements only.");
        });
    } else {
      add(null);
    }
  };

  /* ---------- 6. added to basket — measure another, or check out ---------- */
  Widget.prototype.renderAdded = function () {
    var c = this.cfg, s = this.state, last = s.lastAdd || {}, props = last.props || {};
    var prod = last.variant ? (c.products.filter(function (p) { return p.variantId === last.variant.variantId; })[0] || {}) : {};
    // Prefer the saved properties (works for both flat variants and the generic price-ladder variant).
    var sizeLbl = props["Size"] || prod.sizeLabel || "";
    var fabricLbl = props["Fabric"] || prod.colourLabel || "";
    var priceLbl = props["Estimated price"] || prod.price || "";
    var summary = [sizeLbl, fabricLbl, priceLbl].filter(Boolean).join(" · ");
    var name = props["Window location"] || "Your window";
    this.h(
      '<div class="sw-card">' +
        demoBanner(c) +
        '<p class="sw-eyebrow">' + (s.addedCount > 1 ? s.addedCount + " windows in your basket" : "Added to your basket") + "</p>" +
        '<h3 class="sw-h">' + esc(name) + " added ✓</h3>" +
        '<div class="sw-readout">' +
          '<div class="sw-stat"><b>' + esc(props["Measured width (mm)"] || "?") + '</b><span>Width (mm)</span></div>' +
          '<div class="sw-stat"><b>' + esc(props["Measured height (mm)"] || "?") + '</b><span>Height (mm)</span></div>' +
        "</div>" +
        (summary ? '<p class="sw-hint">' + esc(summary) + " — measurements saved to this item.</p>" : "") +
        '<p class="sw-p" style="margin-top:1rem"><strong>Measuring more windows?</strong> Add them all to one order.</p>' +
        '<div class="sw-choices" style="margin-bottom:0.9rem">' +
          '<button class="sw-choice" id="sw-same"><strong>Another the same size</strong><span>Same ' + esc(summary || "size") + " — just give it a different name.</span></button>" +
          '<button class="sw-choice" id="sw-scratch"><strong>Measure another window</strong><span>Start again from a photo or by hand.</span></button>' +
        "</div>" +
        '<div id="sw-payload"></div>' +
        '<div class="sw-btn-row"><span class="sw-spacer"></span>' +
          '<button class="sw-btn sw-btn-primary" id="sw-basket">Go to basket</button>' +
        "</div>" +
      "</div>"
    );
    var self = this;
    if (c.demoMode && last.variant) {
      this.$("#sw-payload").innerHTML = '<div class="sw-payload">POST /cart/add.js\n' +
        esc(JSON.stringify({ items: [{ id: last.variant.variantId, quantity: 1, properties: props }] }, null, 2)) + "</div>";
    }
    this.$("#sw-same").addEventListener("click", function () { self.renderAddSame(); });
    this.$("#sw-scratch").addEventListener("click", function () {
      s.manual = false; s.halfWindow = null; s.file = null; s.img = null;
      s.points = { ref: [], width: [], height: [] }; s.result = null; s.location = ""; s.consent = false;
      // Carry on with the same fabric — no need to re-pick. The "Fabric · Change" bar on the next
      // screens lets them switch if they want. Everything else is reset.
      self.renderIntro();
    });
    this.$("#sw-basket").addEventListener("click", function () {
      if (c.demoMode) { self.$("#sw-basket").textContent = "Basket (preview)"; return; }
      window.location.href = "/cart";
    });
  };

  /* ---------- 6b. add another, same size, different name ---------- */
  Widget.prototype.renderAddSame = function () {
    var c = this.cfg, s = this.state, last = s.lastAdd || {}, lastProps = last.props || {};
    var prod = last.variant ? (c.products.filter(function (p) { return p.variantId === last.variant.variantId; })[0] || {}) : {};
    var summary = [lastProps["Size"] || prod.sizeLabel || "", lastProps["Fabric"] || prod.colourLabel || ""].filter(Boolean).join(" · ");
    this.h(
      '<div class="sw-card">' +
        demoBanner(c) +
        '<p class="sw-eyebrow">Another the same</p>' +
        '<h3 class="sw-h">Same size, new window</h3>' +
        '<p class="sw-p">We’ll add another <strong>' + esc(summary || "shade") + "</strong> at <strong>" +
          esc(String(s.result.widthMm)) + " × " + esc(String(s.result.heightMm)) + " mm</strong>. Give this one a name so you can tell them apart.</p>" +
        '<div class="sw-field">' +
          '<label class="sw-label" for="sw-sloc">Name this window</label>' +
          '<div class="sw-input-row">' +
            '<input id="sw-sloc" class="sw-input" type="text" placeholder="e.g. Living room – right" />' +
            '<button type="button" class="sw-btn sw-btn-ghost sw-mic" id="sw-mic" title="Speak the name" aria-label="Speak the name">🎤</button>' +
          "</div>" +
        "</div>" +
        '<p class="sw-error" id="sw-err" style="display:none"></p>' +
        '<div class="sw-btn-row">' +
          '<button class="sw-btn sw-btn-ghost" id="sw-back">Back</button><span class="sw-spacer"></span>' +
          '<button class="sw-btn sw-btn-primary" id="sw-add">Add to basket</button>' +
        "</div>" +
      "</div>"
    );
    var self = this;
    this.setupMic("#sw-sloc");
    this.$("#sw-back").addEventListener("click", function () { self.renderAdded(); });
    this.$("#sw-add").addEventListener("click", function () {
      var err = self.$("#sw-err"); err.style.display = "none";
      var name = self.$("#sw-sloc").value.trim();
      if (!name) { err.textContent = "Please give this window a name so it isn’t confused with the last one."; err.style.display = "block"; return; }
      var btn = self.$("#sw-add"); btn.disabled = true; btn.textContent = "Working…";
      s.location = name;
      // Same window spec, new name — reuse the saved properties (incl. Fabric/Size/Estimated price)
      // so the price charged stays identical; drop the photo (it's a different physical window).
      var props = Object.assign({}, lastProps, { "Window location": name });
      delete props["Photo"];
      self.finishAdd(last.variant, props, btn, function (m) { err.textContent = m; err.style.display = "block"; });
    });
  };

  /* ---------- boot ---------- */
  function boot() {
    var el = document.getElementById("sash-measure-widget");
    if (el && !el.getAttribute("data-mounted")) { el.setAttribute("data-mounted", "1"); mount(el); }
  }
  // Only auto-boot in a browser. Under Node (tests) there's no document, so skip it.
  if (typeof document !== "undefined") {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
    else boot();
  }

  // Expose the pure logic for unit tests (no effect in the browser).
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { dist: dist, computeDimensions: computeDimensions, suggestSize: suggestSize,
      buildLineItemProps: buildLineItemProps, validateManual: validateManual, findVariant: findVariant,
      calculatePrice: calculatePrice, findPricedVariant: findPricedVariant };
  }
})();
